import { Injectable } from '@angular/core';
import { IUserInfo, AuthService } from '../api/auth.service';
import { BehaviorSubject } from 'rxjs';
import { RoutesService } from '../api/routes.service';
import { G9DemoDataService } from './g9-demo-data.service';
import {AccountType} from "../constants/account-types";
import { boardTechReadinessCheck, techReadiGoMode, techReadiGoModeOsslt, boardTechReadinessCheckOsslt } from './sa-tech-readiness/data/checklist';
import { IFilterToggle } from '../ui-partial/filter-toggles/filter-toggles.component';
import { ASSESSMENT } from '../ui-teacher/data/types';
import { IContactIT, IIpData } from './sa-tech-readiness/data/types';

const SCHOOL_ADMIN_ROLE = 'schl_admin';

export type GroupRolesList = Array<{
  role_type:string,
  group_id:number,
}>;

export enum ClassFilterId {
  G9 = 'G9',
  OSSLT = 'OSSLT',
}

@Injectable({
  providedIn: 'root'
})
export class MySchoolService {
  school: any;

  // services + init
  constructor(
    private auth: AuthService,
    private routes: RoutesService,
    private g9DemoData: G9DemoDataService, // temp
  ) { 
    this.auth.user().subscribe(v => {
      this._userInfo = v;
      if(v !== null) {
        // temp change to avoid API error
        if ([AccountType.BC_GRAD_MINISTRY_ADMIN, AccountType.BC_FSA_MINISTRY_ADMIN].includes(this._userInfo.accountType)) {
          console.log('return nothing');
          return;          
        }
        this.loadMyRoles();
      }
    });
  }

  // vars (these should never be public)
  private _userInfo:IUserInfo;
  private _roles:GroupRolesList;
  private info: BehaviorSubject<GroupRolesList> = new BehaviorSubject(null);
  fail: BehaviorSubject<boolean> = new BehaviorSubject(null);

  private currentSchoolGroupId:number;

  private classFilterToggles:IFilterToggle[] = [
    {
      id: ClassFilterId.G9,
      caption: 'txt_g9_math',
      value: false,
    },
    {
      id: ClassFilterId.OSSLT,
      caption: 'lbl_osslt',
      value: false,
    },
  ]

  // public methods
  public sub() {
    return this.info;
  }

  private hasSchoolAdminRole(roles: GroupRolesList) {
    return roles.find(role => ['schl_admin', 'bc_fsa_admin'].includes(role.role_type))
  }
  public patchSchoolTestAttempts(){
    return this.auth.apiPatch(this.routes.SCHOOL_ADMIN_SCHOOL, 1, {});
  }
  
  private loadMyRoles(){
    return this.auth
      .apiFind(this.routes.SCHOOL_ADMIN_ROLES)
      .then((roles:GroupRolesList) => {
        this._roles = roles;
        if (roles.length > 0 && this.hasSchoolAdminRole(roles)){
          const activeRole = roles[0]; // arbitrary... assumption is that there is not more than one school per school admin right now
          this.currentSchoolGroupId = activeRole.group_id;
          return this.auth
              .apiGet(this.routes.SCHOOL_ADMIN_SCHOOL,null, {
                query: {
                  group_id: this.currentSchoolGroupId,
                }
              })
        }
        else {
          this.fail.next(true);
        }
      })
      .then(schoolsData => {
        if (schoolsData) {
          this.g9DemoData.init(schoolsData[0]); // always just one school from this request
          this.school = schoolsData[0].school;
          console.log(schoolsData, this.school[0].id)
          this.info.next(this._roles);
        }
      })
  }

  getCurrentSchoolId(){
    const school = this.school[0];
    if (school){
      return school.id
    }
  }

  loadSchoolReports(singleStudentUid?:number){
    const queryparams = {
      school_group_id: this.currentSchoolGroupId,
      schoolsId:this.school[0].id, 
      assessment_slug:ASSESSMENT.OSSLT_OPERATIONAL,
      singleStudentUid,
      tw_id: -1 // was hard-coded to 7, which is not better. This value has been disabled on the server side for the time being.
    }
    if (this._roles.length > 0 && this.hasSchoolAdminRole(this._roles)){
      return this.auth
          .apiFind(this.routes.SCHOOL_ADMIN_REPORTS, {
            query: queryparams
          }).then(res =>{
            return res;
          })
    }
    else {
      this.fail.next(true);
    }
  }

  getCurrentSchoolGroupId() {
    return this.currentSchoolGroupId;
  }

  fetchSecureLink(route:string){
    return this.auth.apiGet(route, this.currentSchoolGroupId,this.configureQueryParams())
  }
  configureQueryParams(){
    const schoolData: any = this.g9DemoData.schoolData
    if (schoolData){
      return {
        query: {
          schl_group_id: schoolData.group_id,
          is_private: schoolData.is_private
        }
      }
    }
    return null;
  }
  configureIPQueryParams(is_verified:number){
    const schoolData: any = this.g9DemoData.schoolData
    if (schoolData){
      return {
        query: {
          schl_group_id: schoolData.group_id,
          is_private: schoolData.is_private,
          is_verified
        }
      }
    }
    return null;
  }
  loadChecklistStates(){
    let checklistPromise;
    if (this.currentSchoolGroupId){
      //return Promise.resolve({});
      return this.auth
      .apiGet(
        this.routes.SCHOOL_ADMIN_CHECKLIST,
        this.currentSchoolGroupId,
        this.configureQueryParams()
      )
      .then( (checklistStates: any) => {
        console.log(checklistStates)
        const state = {};
        state[boardTechReadinessCheck] = checklistStates.boardTechReadiCompleted;
        state[boardTechReadinessCheckOsslt] = checklistStates.boardOssltTechReadiCompleted;
        state[techReadiGoMode] = checklistStates.warnTechReadiCompleted
        state[techReadiGoModeOsslt] = checklistStates.warnOssltTechReadiCompleted;
        checklistStates.checklistItems.forEach(checkItem => {
          state[checkItem.slug] = checkItem.value;
        })
        // console.log('checklist state raw', checklistStates)
        // console.log('checklist state', JSON.parse(JSON.stringify(state)))
        return state;
      });
     
    }

   return this.loadMyRoles().then( roles => {
     //return Promise.resolve({});
     return  this.auth
     .apiGet(
       this.routes.SCHOOL_ADMIN_CHECKLIST,
       this.currentSchoolGroupId,
       this.configureQueryParams()
     )
     .then( (checklistStates: any) => {
       const state = {};
       checklistStates.forEach(checkItem => {
         state[checkItem.slug] = checkItem.value;
         state[boardTechReadinessCheck] = checkItem.boardTechReadiCompleted;
         state[techReadiGoMode] = checkItem.warnTechReadiCompleted;
       })
       return state;
     });
    })  
  }



  createIpAddress(payload:IIpData,is_verified:number){
    return this.auth
    .apiCreate(
      this.routes.SCHOOL_ADMIN_IP,
        payload,
      this.configureIPQueryParams(is_verified)
    )
  }
  getIpAddresses(){
    return this.auth
    .apiFind(
      this.routes.SCHOOL_ADMIN_IP,
      this.configureQueryParams()
    )
  }
  revokeIpAddress(id:number){
    return this.auth
    .apiRemove(
      this.routes.SCHOOL_ADMIN_IP,
      id,
      this.configureQueryParams()
    )
  }
  updateITContacts(payload:IContactIT){
   return this.auth
   .apiUpdate(
     this.routes.SCHOOL_ADMIN_CONTACT,
     this.currentSchoolGroupId,
    payload,
    this.configureQueryParams()
   )
  }

  getITContacts(){
    return this.auth
    .apiGet(
      this.routes.SCHOOL_ADMIN_CONTACT,
      this.currentSchoolGroupId,
      this.configureQueryParams()
    )
  }

  toggleTRChecklist(slug:string, value:boolean){
    return this.auth
      .apiUpdate(
        this.routes.SCHOOL_ADMIN_CHECKLIST,
        this.currentSchoolGroupId,
        {
          slug,
          value,
        },
        this.configureQueryParams()
      )
  }

  getClassFilterToggles(){
    return this.classFilterToggles;
  }

  // to do
  constructPermissionsParams(query?:any){
    // if (!this._info){ console.warn('myInst constructPermissionsParams early exit'); return; }
    const instit_group_id = 0; // this._info.groupId;
    return {
      query:{ 
        ... query,
        instit_group_id, 
      }
    }
  }



}
